import * as ngCommonHttp from '@angular/common/http';

import { catchError, map } from 'rxjs/operators';

import { AbstractCatalogItemsService } from '@abstract-services/catalog-items/1.0/catalog-items.abstract-service';
import { Injectable } from '@angular/core';
import { CatalogItemsAPI } from '@constants/api';
import { CatalogItemType } from '@constants/catalogItem/1.0/index';
import { Composite } from '@interfaces/composite/1.0';
import { ModifierGroup } from '@interfaces/modifier-group/1.0';
import { Modifier } from '@interfaces/modifier/1.0';
import { ProductGroup } from '@interfaces/product-group/1.0';
import { Product } from '@interfaces/product/1.0';
import { BehaviorSubject, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

@Injectable({
  providedIn: 'root',
})
export class CatalogItemsService implements AbstractCatalogItemsService {
  deleteCatalogItemsUrl: string;
  reenableCatalogItemsUrl: string;
  serviceURL: any;
  masterCache: CatalogItemsMasterCache[];
  itemListMap: { [key: string]: any };
  catalogItemType = CatalogItemType;
  skuListCache = new BehaviorSubject<any>(undefined);
  softDeleteFilters: { [key: string]: boolean } = {
    product: false,
    productGroup: false,
    modifier: false,
    modifierGroup: false,
    composite: false,
  };
  public readonly isReadOnlyItemSubject = new BehaviorSubject<boolean>(false);
  readonly isReadOnlyItem$ = this.isReadOnlyItemSubject.asObservable();
  public readonly isUncoveredPropertiesReadOnlySubject = new BehaviorSubject<boolean>(false);
  readonly isUncoveredPropertiesReadOnly$ =
    this.isUncoveredPropertiesReadOnlySubject.asObservable();

  /**
   * Page warnings dismissed by the user, keyed by message so distinct warnings dismiss
   * independently and a changed message shows itself again. Scoped to the catalog module:
   * `CatalogBaseComponent` clears it when the module is left or the brand changes.
   */
  private dismissedPageWarnings = new Set<string>();

  constructor(private http: ngCommonHttp.HttpClient) {
    this.deleteCatalogItemsUrl = CatalogItemsAPI.SOFT_DELETE;
    this.reenableCatalogItemsUrl = CatalogItemsAPI.RE_ENABLE;
    if (!this.masterCache) {
      this.masterCache = [];
    }
  }

  deleteCatalogItem(catalogEntryId: string, entityType: string): Observable<any> {
    const URL = this.deleteCatalogItemsUrl.replace(':catalogEntryId', catalogEntryId);
    return this.http.delete(URL).pipe(
      map((response: any) => {
        this.deleteItemFromCache(catalogEntryId, this.catalogItemType[entityType]);
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  reenableCatalogItem(catalogEntryId: string): Observable<any> {
    const URL = this.reenableCatalogItemsUrl.replace(':catalogEntryId', catalogEntryId);
    return this.http.put(URL, {}).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  // update( entityType: string, values): MasterCache - create / update the cache of any catalog item
  updateCache(itemType: CatalogItemType, values?): CatalogItemsMasterCache[] {
    if (values) {
      return (this.masterCache[itemType] = [...values]);
    } else {
      return (this.masterCache[itemType] = []);
    }
  }

  // getCache(entityType: string): MasterCache - updates the item in the cache with the given ID to the given item
  getCache(itemType: CatalogItemType) {
    return this.masterCache[itemType];
  }

  /**
   * add new item into the cache for the given entityType
   *
   * @param catalogItem - catalogItem to be added to the cache
   */
  addItemCache(catalogItem, itemType: CatalogItemType) {
    if (this.masterCache[itemType]) {
      this.masterCache[itemType].push(catalogItem);
    }
  }

  /**
   * Updates the value in the cache for the given catalogEntryId and given catalog item
   */
  updateItemCache(
    catalogItem,
    itemType: CatalogItemType,
    fieldToCheck: string = 'catalogEntryId',
  ): void {
    if (this.masterCache[itemType]) {
      const itemIndex = this.masterCache[itemType].findIndex(
        (ci) => ci[fieldToCheck] === catalogItem[fieldToCheck],
      );
      if (itemIndex > -1) {
        this.masterCache[itemType][itemIndex] = catalogItem;
      }
    }
  }

  // delete (catalogEntryId: string, entityType): void - deletes the item with ID from the cache
  deleteItemFromCache(
    catalogEntryId: string,
    itemType: CatalogItemType,
    fieldToCheck: string = 'catalogEntryId',
  ): void {
    const itemIndex = this.masterCache[itemType].findIndex(
      (ci) => ci[fieldToCheck] === catalogEntryId,
    );
    this.masterCache[itemType].splice(itemIndex, 1);

    // eslint-disable-next-line default-case
    switch (itemType) {
      case this.catalogItemType.PRODUCT:
        this.clearCache(this.catalogItemType.PRODUCTGROUP);
        break;
      case this.catalogItemType.PRODUCTGROUP:
        this.clearCache(this.catalogItemType.COMPOSITE);
        break;
      case this.catalogItemType.MODIFIER:
        this.clearCache(this.catalogItemType.MODIFIERGROUP);
        break;
      case this.catalogItemType.MODIFIERGROUP:
        this.clearCache(this.catalogItemType.PRODUCT);
        break;
    }
    return;
  }

  clearCache(itemType: string): CatalogItemsMasterCache {
    return (this.masterCache[itemType] = undefined);
  }

  resetMasterCache(): void {
    this.masterCache = [];
  }

  isPageWarningDismissed(message: string): boolean {
    return !!message && this.dismissedPageWarnings.has(message);
  }

  dismissPageWarning(message: string): void {
    if (message) {
      this.dismissedPageWarnings.add(message);
    }
  }

  resetDismissedPageWarnings(): void {
    this.dismissedPageWarnings.clear();
  }

  set isReadOnlyItem(value: boolean) {
    this.isReadOnlyItemSubject.next(value);
  }

  get isReadOnlyItem(): boolean {
    return this.isReadOnlyItemSubject.value;
  }

  set isUncoveredPropertiesReadOnly(value: boolean) {
    this.isUncoveredPropertiesReadOnlySubject.next(value);
  }

  get isUncoveredPropertiesReadOnly(): boolean {
    return this.isUncoveredPropertiesReadOnlySubject.value;
  }
}

export interface CatalogItemsMasterCache {
  product?: Product[];
  productGroup?: ProductGroup[];
  modifier?: Modifier[];
  modifierGroup?: ModifierGroup[];
  composite?: Composite[];
}
